import { Geofence, GeofenceType } from '@eagle/core-data-types';
import { EventDefinition, EventLocationData } from '@eagle/react-common';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  getMostRecentEventAfterTime,
  getMostRecentEventBeforeTime,
  getNearestEventByTime,
  isGeofenceEventType,
  toEventDataDuration,
  toEventDataIncrease,
  toEventDataValue,
} from './trip-history-base-view-utils';
import {
  TripEventFetchResult,
  TripGeofenceItem,
  TripGeofenceSummary,
  TripMetricEvents,
  TripMetrics,
  TripSummary,
} from './trip-history-base-view.types';
import { toEventTime } from './trip-history-event-display-utils';
import { getTripEventsInRange } from './trip-history-event-filtering-utils';
import {
  buildTripRouteFullAddressLabel,
  buildTripRouteLabelWithReverseGeocode,
} from './trip-history-route-address-utils';
import {
  buildTripEventSummary,
  enrichEventsWithNearestLocationUpdate,
  type TripEventSummarizationOptions,
  type TripEventSummaryItem,
  type TripLocationEnrichmentOptions,
} from './trip-history-timeline-processing-utils';

interface ReverseGeocodeApi {
  get: (params: { lat: number; long: number }) => Promise<{ address?: unknown }>;
}

interface CacheLookupApi {
  one: (...args: unknown[]) => Promise<unknown>;
}

interface UseTripEventDetailsLoaderParams {
  eventDefinitions: EventDefinition[];
  geofenceCache: CacheLookupApi;
  geofenceTypeCache: CacheLookupApi;
  getLastThingEvent: (entityId: string, params: Record<string, unknown>) => Promise<{ items: unknown[] }>;
  inProgressLabel: string;
  resolvedTripSummaries: TripSummary[];
  reverseGeocodeApi: ReverseGeocodeApi;
  singleRouteLabel: string;
  getThingEvents: (entityId: string, params: Record<string, unknown>) => Promise<{ items: unknown[] }>;
  tripLocationEnrichmentOptions?: TripLocationEnrichmentOptions;
  tripEventSummarizationOptions: TripEventSummarizationOptions;
  tripInProgressLabel: string;
  unknownLocationLabel: string;
  setTripEventDetailsLoadingByTripId: Dispatch<SetStateAction<Record<string, boolean>>>;
  setTripEventSummaryByTripId: Dispatch<SetStateAction<Record<string, TripEventSummaryItem[]>>>;
  setTripGeofenceSummaryByTripId: Dispatch<SetStateAction<Record<string, TripGeofenceSummary>>>;
  setTripMetricEventsByTripId: Dispatch<SetStateAction<Record<string, TripMetricEvents>>>;
  setTripMetricsByTripId: Dispatch<SetStateAction<Record<string, TripMetrics>>>;
  setTripRouteFullAddressLabelByTripId: Dispatch<SetStateAction<Record<string, string>>>;
  setTripRouteLabelByTripId: Dispatch<SetStateAction<Record<string, string>>>;
}

export const useTripEventDetailsLoader = ({
  eventDefinitions,
  geofenceCache,
  geofenceTypeCache,
  getLastThingEvent,
  inProgressLabel,
  resolvedTripSummaries,
  reverseGeocodeApi,
  setTripEventDetailsLoadingByTripId,
  setTripEventSummaryByTripId,
  setTripGeofenceSummaryByTripId,
  setTripMetricEventsByTripId,
  setTripMetricsByTripId,
  setTripRouteFullAddressLabelByTripId,
  setTripRouteLabelByTripId,
  singleRouteLabel,
  getThingEvents,
  tripLocationEnrichmentOptions,
  tripEventSummarizationOptions,
  tripInProgressLabel,
  unknownLocationLabel,
}: UseTripEventDetailsLoaderParams): void => {
  useEffect(() => {
    if (!resolvedTripSummaries.length) {
      setTripGeofenceSummaryByTripId({});
      setTripEventSummaryByTripId({});
      setTripMetricsByTripId({});
      setTripMetricEventsByTripId({});
      setTripRouteFullAddressLabelByTripId({});
      setTripRouteLabelByTripId({});
      setTripEventDetailsLoadingByTripId({});
      return;
    }

    let isCancelled = false;
    setTripEventDetailsLoadingByTripId(resolvedTripSummaries.reduce<Record<string, boolean>>((accumulator, trip) => {
      accumulator[trip.id] = true;
      return accumulator;
    }, {}));

    void Promise.all(resolvedTripSummaries.map(async (trip): Promise<TripEventFetchResult> => {
      try {
        const tripEnd = trip.isOngoing ? DateTime.now() : trip.finish;
        const [response, groupedMetricsResponse, postFinishMetricsResponse, preTripDistanceEvent, preTripEngineHoursEvent, postTripDistanceEvent, postTripEngineHoursEvent] = await Promise.all([
          getThingEvents(trip.entityId, {
            dateRangeFinish: tripEnd.toJSDate(),
            dateRangeStart: trip.start.toJSDate(),
            sort: 'occurred',
          }),
          getThingEvents(trip.entityId, {
            dateRangeFinish: tripEnd.toJSDate(),
            dateRangeStart: trip.start.toJSDate(),
            filter: {
              eventTypeId: { $in: ['update', 'summary'] },
              feature: { $in: ['distance/primary', 'fuel-consumed', 'engine-running'] },
            },
            sort: 'occurred',
          }),
          trip.isOngoing
            ? Promise.resolve({ items: [] })
            : getThingEvents(trip.entityId, {
              dateRangeFinish: DateTime.now().toJSDate(),
              dateRangeStart: trip.finish.toJSDate(),
              filter: {
                eventTypeId: 'update',
                feature: { $in: ['distance/primary', 'engine-running'] },
              },
              sort: 'occurred',
            }),
          getMostRecentEventBeforeTime(getLastThingEvent, trip.entityId, trip.start, 'distance/primary'),
          getMostRecentEventBeforeTime(getLastThingEvent, trip.entityId, trip.start, 'engine-running'),
          trip.isOngoing
            ? Promise.resolve(undefined)
            : getMostRecentEventAfterTime(getLastThingEvent, trip.entityId, trip.finish, 'distance/primary'),
          trip.isOngoing
            ? Promise.resolve(undefined)
            : getMostRecentEventAfterTime(getLastThingEvent, trip.entityId, trip.finish, 'engine-running'),
        ]);

        const toEventFeature = (event: EventLocationData): string | undefined => {
          return typeof event.feature === 'string' ? event.feature : undefined;
        };

        const tripEvents = response.items as EventLocationData[];
        const groupedMetricsEvents = groupedMetricsResponse.items as EventLocationData[];
        const postFinishMetricsEvents = postFinishMetricsResponse.items as EventLocationData[];
        const resolvedTripEvents = enrichEventsWithNearestLocationUpdate(tripEvents, tripLocationEnrichmentOptions);
        const totalDistanceEvents = groupedMetricsEvents
          .filter((event) => event.eventTypeId === 'update' && toEventFeature(event) === 'distance/primary')
          .filter((event) => toEventDataValue(event) !== undefined);
        const tripDistanceEvents = groupedMetricsEvents
          .filter((event) => event.eventTypeId === 'summary' && toEventFeature(event) === 'distance/primary')
          .filter((event) => toEventDataIncrease(event) !== undefined);
        const fuelEvents = groupedMetricsEvents
          .filter((event) => event.eventTypeId === 'summary' && toEventFeature(event) === 'fuel-consumed')
          .filter((event) => toEventDataIncrease(event) !== undefined);
        const engineHoursEvents = groupedMetricsEvents
          .filter((event) => event.eventTypeId === 'update' && toEventFeature(event) === 'engine-running')
          .filter((event) => toEventDataDuration(event) !== undefined);
        const postFinishDistanceEvents = postFinishMetricsEvents
          .filter((event) => toEventFeature(event) === 'distance/primary')
          .filter((event) => toEventDataValue(event) !== undefined);
        const postFinishEngineHoursEvents = postFinishMetricsEvents
          .filter((event) => toEventFeature(event) === 'engine-running')
          .filter((event) => toEventDataDuration(event) !== undefined);

        const bounds = { end: tripEnd, start: trip.start };
        const nearestDistanceStartEvent = getNearestEventByTime(totalDistanceEvents, trip.start, bounds);
        const nearestEngineHoursStartEvent = getNearestEventByTime(engineHoursEvents, trip.start, bounds);
        const nearestEngineHoursFinishEvent = getNearestEventByTime(engineHoursEvents, tripEnd, bounds);
        const nearestTripDistanceFinishEvent = getNearestEventByTime(tripDistanceEvents, tripEnd, bounds);
        const nearestFuelFinishEvent = getNearestEventByTime(fuelEvents, tripEnd, bounds);
        const resolvedDistanceStartEvent = preTripDistanceEvent ?? nearestDistanceStartEvent;
        const resolvedEngineHoursStartEvent = preTripEngineHoursEvent ?? nearestEngineHoursStartEvent;

        const startDistanceValue = resolvedDistanceStartEvent ? toEventDataValue(resolvedDistanceStartEvent) : undefined;
        const tripDistanceIncreaseValue = nearestTripDistanceFinishEvent ? toEventDataIncrease(nearestTripDistanceFinishEvent) : undefined;
        const startEngineHoursValue = resolvedEngineHoursStartEvent ? toEventDataDuration(resolvedEngineHoursStartEvent) : undefined;
        const finishEngineHoursValue = nearestEngineHoursFinishEvent ? toEventDataDuration(nearestEngineHoursFinishEvent) : undefined;
        const fuelIncreaseValue = nearestFuelFinishEvent ? toEventDataIncrease(nearestFuelFinishEvent) : undefined;
        const firstPostFinishDistanceEvent = trip.isOngoing
          ? undefined
          : [...postFinishDistanceEvents]
            .filter((event) => toEventTime(event.occurred) >= trip.finish)
            .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis())[0];
        const postFinishDistanceValue = firstPostFinishDistanceEvent ? toEventDataValue(firstPostFinishDistanceEvent) : undefined;
        const firstPostFinishEngineHoursEvent = trip.isOngoing
          ? undefined
          : [...postFinishEngineHoursEvents]
            .filter((event) => toEventTime(event.occurred) >= trip.finish)
            .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis())[0];
        const postFinishEngineHoursValue = firstPostFinishEngineHoursEvent ? toEventDataDuration(firstPostFinishEngineHoursEvent) : undefined;
        const resolvedOdometerEndValue = postTripDistanceEvent ? toEventDataValue(postTripDistanceEvent) : postFinishDistanceValue;
        const resolvedEngineHoursEndValue = postTripEngineHoursEvent ? toEventDataDuration(postTripEngineHoursEvent) : (postFinishEngineHoursValue ?? finishEngineHoursValue);

        const distanceMeters = tripDistanceIncreaseValue === undefined
          ? undefined
          : Math.max(tripDistanceIncreaseValue, 0);

        const metrics: TripMetrics = {
          distanceKm: distanceMeters === undefined ? undefined : distanceMeters / 1000,
          engineHoursEndMinutes: resolvedEngineHoursEndValue,
          engineHoursStartMinutes: startEngineHoursValue,
          fuelLiters: fuelIncreaseValue === undefined ? undefined : fuelIncreaseValue / 1000,
          odometerEndMeters: resolvedOdometerEndValue,
          odometerStartMeters: startDistanceValue,
        };

        const metricEvents: TripMetricEvents = {
          distancePrimaryIncreaseEvent: nearestTripDistanceFinishEvent,
          engineHoursEndEvent: postTripEngineHoursEvent ?? firstPostFinishEngineHoursEvent ?? nearestEngineHoursFinishEvent,
          engineHoursStartEvent: resolvedEngineHoursStartEvent,
          fuelConsumedEvent: nearestFuelFinishEvent,
          odometerEndEvent: postTripDistanceEvent ?? firstPostFinishDistanceEvent,
          odometerStartEvent: resolvedDistanceStartEvent,
        };

        const geofenceEvents = getTripEventsInRange(trip, resolvedTripEvents).filter((event) => {
          if (isGeofenceEventType(event.eventTypeId)) return true;
          const geofenceId = (event.data as { geofenceId?: string } | undefined)?.geofenceId;
          return Boolean(geofenceId);
        });
        const geofenceSummaryItems = await Promise.all(geofenceEvents.map(async (event) => {
          const geofenceId = (event.data as { geofenceId?: string } | undefined)?.geofenceId;
          const geofence = geofenceId ? await geofenceCache.one(geofenceId).catch(() => undefined) as Geofence | undefined : undefined;
          const geofenceType = geofence?.geofenceTypeId
            ? await geofenceTypeCache.one(geofence.geofenceTypeId).catch(() => undefined) as GeofenceType | undefined
            : undefined;
          const occurred = toEventTime(event.occurred);
          const geofenceDisplay = geofence?.display ?? geofenceId ?? 'Unknown geofence';
          const geofenceTypeDisplay = geofenceType?.display;
          const label = geofenceTypeDisplay ? `${geofenceTypeDisplay} / ${geofenceDisplay}` : geofenceDisplay;
          const isEnter = event.eventTypeId === 'geofence-enter' || event.eventTypeId.includes('enter');

          return {
            actionLabel: isEnter ? 'Enter' : 'Exit',
            label,
            occurredLabel: occurred.toFormat('hh:mm a'),
            occurredMillis: occurred.toMillis(),
          } as TripGeofenceItem;
        }));

        const geofenceSummary: TripGeofenceSummary = {
          items: geofenceSummaryItems.sort((first, second) => first.occurredMillis - second.occurredMillis),
        };

        const routeFullAddressLabel = await buildTripRouteFullAddressLabel(
          trip,
          resolvedTripEvents,
          reverseGeocodeApi.get,
          unknownLocationLabel,
          tripInProgressLabel,
          singleRouteLabel,
        );
        const summary = buildTripEventSummary(trip, resolvedTripEvents, eventDefinitions, tripEventSummarizationOptions);
        const routeLabel = await buildTripRouteLabelWithReverseGeocode(
          trip,
          resolvedTripEvents,
          reverseGeocodeApi.get,
          unknownLocationLabel,
          inProgressLabel,
          singleRouteLabel,
        );
        return { geofenceSummary, metricEvents, metrics, routeFullAddressLabel, routeLabel, summary, tripId: trip.id };
      } catch {
        return {
          geofenceSummary: { items: [] },
          metricEvents: {},
          metrics: {},
          routeFullAddressLabel: trip.isOngoing ? tripInProgressLabel : singleRouteLabel,
          routeLabel: trip.isOngoing ? tripInProgressLabel : singleRouteLabel,
          summary: [],
          tripId: trip.id,
        };
      }
    })).then((entries) => {
      if (isCancelled) return;

      const nextSummaryByTripId = entries.reduce<Record<string, TripEventSummaryItem[]>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.summary;
        return accumulator;
      }, {});

      const nextGeofenceSummaryByTripId = entries.reduce<Record<string, TripGeofenceSummary>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.geofenceSummary;
        return accumulator;
      }, {});

      const nextMetricsByTripId = entries.reduce<Record<string, TripMetrics>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.metrics;
        return accumulator;
      }, {});

      const nextRouteLabelByTripId = entries.reduce<Record<string, string>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.routeLabel;
        return accumulator;
      }, {});

      const nextRouteFullAddressLabelByTripId = entries.reduce<Record<string, string>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.routeFullAddressLabel;
        return accumulator;
      }, {});

      setTripGeofenceSummaryByTripId(nextGeofenceSummaryByTripId);
      setTripEventSummaryByTripId(nextSummaryByTripId);
      setTripMetricsByTripId(nextMetricsByTripId);
      setTripMetricEventsByTripId(entries.reduce<Record<string, TripMetricEvents>>((accumulator, entry) => {
        accumulator[entry.tripId] = entry.metricEvents;
        return accumulator;
      }, {}));
      setTripRouteFullAddressLabelByTripId(nextRouteFullAddressLabelByTripId);
      setTripRouteLabelByTripId(nextRouteLabelByTripId);
      setTripEventDetailsLoadingByTripId(entries.reduce<Record<string, boolean>>((accumulator, entry) => {
        accumulator[entry.tripId] = false;
        return accumulator;
      }, {}));
    }).catch(() => {
      if (isCancelled) return;
      setTripGeofenceSummaryByTripId({});
      setTripEventSummaryByTripId({});
      setTripMetricsByTripId({});
      setTripMetricEventsByTripId({});
      setTripRouteFullAddressLabelByTripId({});
      setTripRouteLabelByTripId({});
      setTripEventDetailsLoadingByTripId(resolvedTripSummaries.reduce<Record<string, boolean>>((accumulator, trip) => {
        accumulator[trip.id] = false;
        return accumulator;
      }, {}));
    });

    return () => {
      isCancelled = true;
    };
  }, [
    eventDefinitions,
    geofenceCache,
    geofenceTypeCache,
    getLastThingEvent,
    inProgressLabel,
    resolvedTripSummaries,
    reverseGeocodeApi,
    setTripEventDetailsLoadingByTripId,
    setTripEventSummaryByTripId,
    setTripGeofenceSummaryByTripId,
    setTripMetricEventsByTripId,
    setTripMetricsByTripId,
    setTripRouteFullAddressLabelByTripId,
    setTripRouteLabelByTripId,
    singleRouteLabel,
    getThingEvents,
    tripLocationEnrichmentOptions,
    tripEventSummarizationOptions,
    tripInProgressLabel,
    unknownLocationLabel,
  ]);
};
